import { View } from 'components/view/view.component'
import { Text } from 'components/text/text.component'
import style from './filters.component.module.css'
import { Skeleton } from 'components/skeleton'
import { useEffect, useState } from 'react'
import { getMonthsBefore, getOneYearBefore, isInSameMonth, isInSameYear } from 'utils/date.util'
import { Select, SelectItem } from 'components/select/select.component'
import { DatePicker } from 'components/datePicker/datePicker.component'
import { useGetCompanyIndicatorsApi } from 'api/company.api'
import { useGetBuildingGroupsApi } from 'api/buildingGroup.api'
import { useGetBuildingsApi } from 'api/building.api'
import { arraysEqual } from 'utils/stringManage.util'
import { FilterType } from 'model/filter.model'
import { Checkbox } from 'components/checkbox/checkbox.component'
import { useGetRoomsApi } from 'api/room.api'
import { useGetAreasApi } from 'api/area.api'
import { Icon } from 'components/icon/icon.component'
import { TextInput } from 'components/textInput/textInput.component'
import { useGetCompanyEquipamentsApi } from 'api/equipaments.api'

interface FiltersProps {
    companyId?: string
    showBuildings?: boolean
    onlyEnableBuildings?: boolean
    onlyOneBuilding?: boolean
    showAreas?: boolean
    onlyEnableAreas?: boolean
    onlyOneArea?: boolean
    showRooms?: boolean
    hideBuildingFromRoomsOptions?: boolean
    hideAreaFromRoomsOptions?: boolean
    roomsTitle?: string
    onlyEnableRooms?: boolean
    onlyOneRoom?: boolean
    roomIndicatorIds?: string[]
    showIndicators?: boolean
    showDashboardIndicators?: boolean
    showBuildingGroups?: boolean
    showStartDate?: boolean
    showEndDate?: boolean
    showDate?: boolean
    minDate?: Date
    maxDate?: Date
    enabledDates?: Date[]
    showOccupationStatus?: boolean
    showCapacity?: boolean
    showEquipaments?: boolean,
    dateType?: 'month' | 'datetime',
    initialStartDate?: Date,
    initialEndDate?: Date,
    initialDate?: Date,
    initialRoomId?: string,
    indicatorsFor?: 'input' | 'dashboard' | 'graphic' | 'report' | 'alert'
    onChange?: (filter: FilterType) => void,
    canExpand?: boolean,
    expanded?: boolean,
    selectAllAreasWhenBuildingChanges?: boolean
    selectAllRoomsWhenAreaChanges?: boolean
    /** Maximum allowed interval between start and end dates, in minutes. Optional. */
    maxDateInterval?: number,
    /** Callback to inform parent when there is (or no longer is) a date-interval validation error. (hasError, message?) */
    onDateIntervalError?: (hasError: boolean, message?: string) => void,
    dateErrorMessage?: string
}

export function Filters({companyId, showBuildings, onlyEnableBuildings, onlyOneBuilding, showAreas, onlyEnableAreas, onlyOneArea, showRooms, hideBuildingFromRoomsOptions, hideAreaFromRoomsOptions, roomsTitle, onlyEnableRooms, onlyOneRoom, roomIndicatorIds, showIndicators, showDashboardIndicators, showBuildingGroups, showStartDate, showEndDate, showDate, minDate, maxDate, enabledDates, showOccupationStatus, showCapacity, showEquipaments, dateType = 'month', initialStartDate, initialEndDate, initialDate, initialRoomId, indicatorsFor, onChange, canExpand, expanded, selectAllAreasWhenBuildingChanges, selectAllRoomsWhenAreaChanges, maxDateInterval, onDateIntervalError, dateErrorMessage }: FiltersProps) {

    const _initialStartDate = initialStartDate ?? getOneYearBefore(getMonthsBefore( new Date(), 2))
    const _initialEndDate = initialEndDate ?? getMonthsBefore(new Date(), 2)

    const [_expanded, setExpanded] = useState<boolean>(canExpand !== undefined ? expanded ?? true : true)
    const [buildingsSelected, setBuildingsSelected] = useState<string[]>([])
    const [areasSelected, setAreasSelected] = useState<string[]>([])
    const [roomsSelected, setRoomsSelected] = useState<string[]>(initialRoomId ? [initialRoomId] : [])
    const [indicatorsSelected, setIndicatorsSelected] = useState<string[]>([])
    const [dashboardIndicatorsSelected, setDashboardIndicatorsSelected] = useState<number[]>([])
    const [buildingGroupSelected, setBuildingGroupSelected] = useState<string>('')
    const [startDate, setStartDate] = useState<Date | undefined>(_initialStartDate)
    const [endDate, setEndDate] = useState<Date | undefined>(_initialEndDate)
    const [date, setDate] = useState<Date | undefined>(initialDate ?? _initialEndDate)
    const [occupationStatus, setOccupationStatus] = useState<'ocupado' | 'desocupado' | undefined>(undefined)
    const [minCapacity, setMinCapacity] = useState<number | undefined>(undefined)
    const [maxCapacity, setMaxCapacity] = useState<number | undefined>(undefined)
    const [startDateError, setStartDateError] = useState<string | undefined>(undefined)
    const [endDateError, setEndDateError] = useState<string | undefined>(undefined)
    const [equipamentsSelected, setEquipamentsSelected] = useState<string[]>([])
    
    const [dateError, setDateError] = useState<string | undefined>(undefined)

    function formatMinutesToReadable(totalMinutes: number){
        const mins = Math.floor(totalMinutes)
        const MINUTES_IN_MONTH = 60 * 24 * 30 // approx 30 days
        const MINUTES_IN_DAY = 60 * 24
        const MINUTES_IN_HOUR = 60

        let rem = mins
        const months = Math.floor(rem / MINUTES_IN_MONTH); rem = rem % MINUTES_IN_MONTH
        const days = Math.floor(rem / MINUTES_IN_DAY); rem = rem % MINUTES_IN_DAY
        const hours = Math.floor(rem / MINUTES_IN_HOUR); rem = rem % MINUTES_IN_HOUR
        const minutes = rem

        const parts: string[] = []
        if(months) parts.push(`${months} mês${months > 1 ? 'es' : ''}`)
        if(days) parts.push(`${days} dia${days > 1 ? 's' : ''}`)
        if(hours) parts.push(`${hours} hora${hours > 1 ? 's' : ''}`)
        if(minutes) parts.push(`${minutes} minuto${minutes > 1 ? 's' : ''}`)
        if(parts.length === 0) return '0 minutos'
        return parts.join(', ')
    }

    useEffect(()=>{
        // If no limit specified, clear errors and notify parent
        if(maxDateInterval === undefined){
            setStartDateError(undefined)
            setEndDateError(undefined)
            if(onDateIntervalError) onDateIntervalError(false)
            return
        }
        // Only validate when both dates present
        if(startDate && endDate){
            const diffMs = endDate.getTime() - startDate.getTime()
            const diffMinutes = diffMs / (1000 * 60)
            if(diffMinutes > maxDateInterval){
                const msg = `Intervalo máximo permitido: ${formatMinutesToReadable(maxDateInterval)}`
                setStartDateError(msg)
                setEndDateError(msg)
                if(onDateIntervalError) onDateIntervalError(true, msg)
            }else{
                setStartDateError(undefined)
                setEndDateError(undefined)
                if(onDateIntervalError) onDateIntervalError(false)
            }
        }else{
            setStartDateError(undefined)
            setEndDateError(undefined)
            if(onDateIntervalError) onDateIntervalError(false)
        }
    }, [startDate, endDate, maxDateInterval, onDateIntervalError])


    const {loading: loadingIndicators, get: getIndicators, data: indicators} = useGetCompanyIndicatorsApi(companyId ?? '') 
    const {loading: loadingBuildingGroups, get: getBuildingGroups, data: buildingGroups} = useGetBuildingGroupsApi(companyId ?? '')
    const {loading: buildingsLoading, get: getBuildings, data: buildings} = useGetBuildingsApi(companyId ?? '')
    const {loading: areasLoading, get: getAreas, data: areas} = useGetAreasApi(companyId ?? '',buildingsSelected ?? '', onlyEnableAreas !== undefined ? !onlyEnableAreas : undefined)
    const {loading: roomsLoading, get: getRooms, data: rooms} = useGetRoomsApi()
    const {loading: loadingEquipaments, get: getEquipaments, data: equipaments} = useGetCompanyEquipamentsApi(companyId ?? '')

    const _indicators = _getIndicators()
    const _dashboardIndicators = indicators?.dashboardIndicators ?? []

    function _getIndicators(){
        if(!indicatorsFor) return []
        if(indicatorsFor === 'input') return indicators?.inputIndicators
        if(indicatorsFor === 'graphic') return indicators?.graphicIndicators
        if(indicatorsFor === 'report') return indicators?.reportIndicators
        if(indicatorsFor === 'alert') return indicators?.alertIndicators
        return []
    }

    useEffect(()=>{
        if(showIndicators || showDashboardIndicators) getIndicators().catch(()=>{})
        if(showBuildingGroups) getBuildingGroups().catch(()=>{})
        if(showBuildings) getBuildings(companyId, onlyEnableBuildings).catch(()=>{})
        if(showRooms && companyId) getRooms(
            {
                companyId: companyId || '',
                buildingIds: buildingsSelected.length > 0 ? buildingsSelected : undefined,
                areaIds: areasSelected.length > 0 ? areasSelected : undefined,
                indicatorIds: roomIndicatorIds,
                disable: onlyEnableRooms !== undefined ? !onlyEnableRooms : undefined
            }
        ).catch(()=>{})
        if(showEquipaments) getEquipaments(companyId ?? '').catch(()=>{})
    // eslint-disable-next-line
    }, [])

    useEffect(()=>{
        if(!onChange) return
        const filter: FilterType = {}
        if(showBuildings) filter.buildings = buildings?.filter(_ => buildingsSelected.includes(_.id))
        if(showBuildingGroups) filter.buildingGroup = buildingGroups?.find(_ => _.id === buildingGroupSelected)
        if(showIndicators) filter.indicators = _indicators?.filter(_ => indicatorsSelected.includes(_.id))
        if(showStartDate) filter.startDate = startDate
        if(showEndDate) filter.endDate = endDate
        if(showDate) filter.date = date
        if(showDashboardIndicators) filter.dashboardIndicators = _dashboardIndicators?.filter((_, index) => dashboardIndicatorsSelected.includes(index))
        if(showAreas) filter.areas = areas?.filter(_ => areasSelected.includes(_.id)).map(_ => ({..._, buildingId: _.building?.id ?? ''}))
        if(showRooms) filter.rooms = rooms?.filter(_ => roomsSelected.includes(_.id))
        if(showOccupationStatus) filter.occupationStatus = occupationStatus
        if(showCapacity) {
            filter.minCapacity = minCapacity
            filter.maxCapacity = maxCapacity
        }
        if(showEquipaments){
            filter.equipaments = equipaments?.filter(_ => equipamentsSelected.includes(_.id))
        }
        onChange(filter)
    // eslint-disable-next-line
    }, [buildingGroupSelected, roomsSelected, areasSelected, indicatorsSelected, dashboardIndicatorsSelected, buildingsSelected, startDate, endDate, date, showBuildings, showAreas, showRooms, showBuildingGroups, showIndicators, showStartDate, showEndDate, showDate, showDashboardIndicators, occupationStatus, minCapacity, maxCapacity, equipamentsSelected])

    useEffect(()=>{
        if(showAreas) getAreas().catch(()=>{})
    // eslint-disable-next-line
    }, [buildingsSelected])

    useEffect(()=>{
        if(showRooms) getRooms(
            {
                companyId: companyId || '',
                buildingIds: buildingsSelected.length > 0 ? buildingsSelected : undefined,
                areaIds: areasSelected.length > 0 ? areasSelected : undefined,
                indicatorIds: roomIndicatorIds,
                disable: onlyEnableRooms !== undefined ? !onlyEnableRooms : undefined
            }
        ).catch(()=>{})
    // eslint-disable-next-line
    }, [areasSelected])
    
    function handleIndicatorsChange(newIndicators: string[] | string){
        let _newIndicators = typeof newIndicators === 'string' ? [newIndicators] : newIndicators
        if(_newIndicators.includes('select_all')) _newIndicators = _indicators?.map(_ => _.id) ?? []
        if(_newIndicators.includes('clear_all')) _newIndicators = []
        setIndicatorsSelected(_newIndicators)
    }

    function handleDashboardIndicatorsChange(newIndicators: string | any[] ){
        let _newIndicators: number[] = []
        if( typeof newIndicators === 'string') {
            if(newIndicators.includes('select_all')) _newIndicators = _dashboardIndicators?.map((_, index) => index) ?? []
            if(newIndicators.includes('clear_all')) _newIndicators = []
        }else{
            _newIndicators = newIndicators
            if(newIndicators.includes('select_all')) _newIndicators = _dashboardIndicators?.map((_, index) => index) ?? []
            if(newIndicators.includes('clear_all')) _newIndicators = []
        }
        setDashboardIndicatorsSelected(_newIndicators)
    }

    function handleBuildingGroupChange(newBuildingGroup: string) {
        setBuildingGroupSelected(newBuildingGroup)
        const group = buildingGroups?.find(_ => _.id === newBuildingGroup)
        const newBuildings = group?.buildings.filter(_ => buildings?.map(_=> _.id).includes(_.id))
        setBuildingsSelected(newBuildings?.map(_ => _.id) ?? [])
    }

    function handleBuildingsChange(newBuildings: string[] | string){
        let _newBuildings = typeof newBuildings === 'string' ? [newBuildings] : newBuildings
        if(_newBuildings.includes('select_all')) _newBuildings = buildings?.map(_ => _.id) ?? []
        if(_newBuildings.includes('clear_all')) _newBuildings = []
        setBuildingsSelected(_newBuildings)
        const group = findGroupOfBuildings(_newBuildings)
        if(group) setBuildingGroupSelected(group.id)
        else setBuildingGroupSelected('')
        // If no buildings selected, clear areas and rooms
        if (!_newBuildings || _newBuildings.length === 0) {
            setAreasSelected([])
            setRoomsSelected([])
            return
        }

        if (selectAllAreasWhenBuildingChanges) {
            // fetch areas for the new buildings and select all their ids
            getAreas({
                companyUsername: companyId ?? '',
                buildingId: _newBuildings.length > 0 ? _newBuildings : undefined,
                disable: onlyEnableAreas !== undefined ? !onlyEnableAreas : undefined
            }).then(result => {
                const areaIds = result?.map(_ => _.id) ?? []
                setAreasSelected(areaIds)
                // If requested, select all rooms for these areas as well
                if (selectAllRoomsWhenAreaChanges) {
                    getRooms({
                        companyId: companyId || '',
                        buildingIds: _newBuildings.length > 0 ? _newBuildings : undefined,
                        areaIds: areaIds.length > 0 ? areaIds : undefined,
                        indicatorIds: roomIndicatorIds,
                        disable: onlyEnableRooms !== undefined ? !onlyEnableRooms : undefined
                    }).then(roomsResult => {
                        setRoomsSelected(roomsResult?.map(r => r.id) ?? [])
                    }).catch(()=>{
                        setRoomsSelected([])
                    })
                } else {
                    // clear rooms selection when buildings change; rooms will be re-fetched by the areas effect
                    setRoomsSelected([])
                }
            }).catch(()=>{
                // on error, clear selections to keep state consistent
                setAreasSelected([])
                setRoomsSelected([])
            })
        } else {
            // keep only the areas that belong to the still-selected buildings
            const validAreaIds = areas?.filter(a => _newBuildings.includes(a.building?.id ?? '')).map(a => a.id) ?? []
            setAreasSelected(prev => prev.filter(id => validAreaIds.includes(id)))
            // also remove rooms that belong to areas that are no longer valid
            setRoomsSelected(prevRooms => prevRooms.filter(rid => {
                const room = rooms?.find(r => r.id === rid)
                if(!room) return false
                return validAreaIds.includes(room.area?.id ?? '')
            }))
        }
    }

    function handleAreasChange(newAreas: string[] | string){
        let _newAreas = typeof newAreas === 'string' ? [newAreas] : newAreas
        if(_newAreas.includes('select_all')) _newAreas = rooms?.map(_ => _.id) ?? []
        if(_newAreas.includes('clear_all')) _newAreas = []
        setAreasSelected(_newAreas)
        // If no areas selected, clear rooms
        if(!_newAreas || _newAreas.length === 0){
            setRoomsSelected([])
            return
        }

        if(selectAllRoomsWhenAreaChanges){
            // fetch rooms for the selected areas and select all their ids
            getRooms({
                companyId: companyId || '',
                buildingIds: buildingsSelected.length > 0 ? buildingsSelected : undefined,
                areaIds: _newAreas.length > 0 ? _newAreas : undefined,
                indicatorIds: roomIndicatorIds,
                disable: onlyEnableRooms !== undefined ? !onlyEnableRooms : undefined
            }).then(result => {
                setRoomsSelected(result?.map(r => r.id) ?? [])
            }).catch(()=>{
                setRoomsSelected([])
            })
        }else{
            // keep only rooms that belong to the still-selected areas
            const validRoomIds = rooms?.filter(r => _newAreas.includes(r.area?.id ?? '')).map(r => r.id) ?? []
            setRoomsSelected(prev => prev.filter(id => validRoomIds.includes(id)))
        }
    }

    function handleRoomsChange(newRooms: string[] | string){
        let _newRooms = typeof newRooms === 'string' ? [newRooms] : newRooms
        if(_newRooms.includes('select_all')) _newRooms = rooms?.map(_ => _.id) ?? []
        if(_newRooms.includes('clear_all')) _newRooms = []
        setRoomsSelected(_newRooms)
    }

    function findGroupOfBuildings(buildings: string[]) {
        if(!buildingGroups || !buildings || buildings.length <= 0) return
        for( let group of buildingGroups){
            if(arraysEqual(group.buildings.map(_ => _.id), buildings)) return group
        }
        return
    }

    function handleEquipamentsChange(newEquipaments: string[] | string){
        let _newEquipaments = typeof newEquipaments === 'string' ? [newEquipaments] : newEquipaments
        if(_newEquipaments.includes('select_all')) _newEquipaments = equipaments?.map(_ => _.id) ?? []
        if(_newEquipaments.includes('clear_all')) _newEquipaments = []
        setEquipamentsSelected(_newEquipaments)
    }

    return <View className={style.Filters}>
    {canExpand && <View className={style.ExpandButton} onClick={()=>{setExpanded(!_expanded)}} style={{marginBottom: _expanded ? '2rem' : '1rem'}}>
        <Text type='h3'>Filtros</Text>
        <Icon>{_expanded? 'keyboard_arrow_up' :'keyboard_arrow_down'}</Icon>
    </View>}
    <View className={style.ContentHeader} style={{display: _expanded ? 'flex' : 'none'}}>
        {(showBuildingGroups && buildingGroups && buildingGroups.length > 0)  && <View className={style.ContentHeaderSection}>
            <View className={style.ContentHeaderSectionItem} >
                <Text type='h4'>Grupo de Prédios</Text>
                {loadingBuildingGroups
                ? <Skeleton color='bg3'/>
                : <Select style={{textAlign: 'left'}} size='small' fullWidth value={buildingGroupSelected} onChange={e => handleBuildingGroupChange(e.target.value)} >
                    {buildingGroups?.sort((a,b) => a.name.localeCompare(b.name)).map(_ => <SelectItem key={_.id} value={_.id}>{_.name}</SelectItem>)}
                </Select>
                }
            </View>
        </View>}
        {showBuildings && <View className={style.ContentHeaderSection}>
            <View className={style.ContentHeaderSectionItem} >
                <Text type='h4'>{onlyOneBuilding ? `Prédio` :`Prédios`}</Text>
                {buildingsLoading
                ? <Skeleton color='bg3'/>
                : <Select style={{textAlign: 'left'}} size='small' fullWidth multiple={!onlyOneBuilding} value={buildingsSelected} onChange={e => handleBuildingsChange(e.target.value)} renderValue={(selected) => buildings?.filter(_=> selected.includes(_.id)).map(_ => _.name).join(', ')} >
                    {!onlyOneBuilding && <SelectItem key={'select_all'} value={'select_all'}>Selecionar todos</SelectItem>}
                    {!onlyOneBuilding && <SelectItem key={'clear_all'} value={'clear_all'}>Desmarcar todos</SelectItem>}
                    {buildings?.sort((a,b) => a.name.localeCompare(b.name)).map(_ => <SelectItem key={_.id} value={_.id}><Checkbox checked={buildingsSelected.includes(_.id)} />{_.name}</SelectItem>)}
                </Select>
                }
            </View>
        </View>}
        {showAreas && <View className={style.ContentHeaderSection}>
            <View className={style.ContentHeaderSectionItem} >
                <Text type='h4'>{onlyOneArea ? `Andar` :`Andares`}</Text>
                {areasLoading
                ? <Skeleton color='bg3'/>
                : <Select style={{textAlign: 'left'}} size='small' fullWidth multiple={!onlyOneArea} value={areasSelected} onChange={e => handleAreasChange(e.target.value)} renderValue={(selected) => areas?.filter(_=> selected.includes(_.id)).map(_ => `${_.building.name} - ${_.name}`).join(', ')} >
                    {!onlyOneArea && <SelectItem key={'select_all'} value={'select_all'}>Selecionar todos</SelectItem>}
                    {!onlyOneArea && <SelectItem key={'clear_all'} value={'clear_all'}>Desmarcar todos</SelectItem>}
                    {areas?.sort((a,b) => (`${a.building.name} - ${a.name}`).localeCompare(`${b.building.name} - ${b.name}`)).map(_ => <SelectItem key={_.id} value={_.id}><Checkbox checked={areasSelected.includes(_.id)} />{`${_.building.name} - ${_.name}`}</SelectItem>)}
                </Select>
                }
            </View>
        </View>}
        {showRooms && <View className={style.ContentHeaderSection}>
            <View className={style.ContentHeaderSectionItem} >
                <Text type='h4'>{roomsTitle ? roomsTitle : onlyOneRoom ? `Sala` :`Salas`}</Text>
                {roomsLoading
                ? <Skeleton color='bg3'/>
                : <Select style={{textAlign: 'left'}} size='small' fullWidth multiple={!onlyOneRoom} value={roomsSelected} onChange={e => handleRoomsChange(e.target.value)} renderValue={(selected) => rooms?.filter(_=> selected.includes(_.id)).map(_ => _.name).join(', ')} >
                    {!onlyOneRoom && <SelectItem key={'select_all'} value={'select_all'}>Selecionar todos</SelectItem>}
                    {!onlyOneRoom && <SelectItem key={'clear_all'} value={'clear_all'}>Desmarcar todos</SelectItem>}
                    {rooms
                        ?.sort((a, b) => {
                            const aLabel = `${a.building && !hideBuildingFromRoomsOptions ? `${a.building.name} - ` : ''}${a.area && !hideAreaFromRoomsOptions ? `${a.area.name} - ` : ''}${a.name}`;
                            const bLabel = `${b.building && !hideBuildingFromRoomsOptions ? `${b.building.name} - ` : ''}${b.area && !hideAreaFromRoomsOptions ? `${b.area.name} - ` : ''}${b.name}`;
                            return aLabel.localeCompare(bLabel);
                        })
                        .map(_ => (
                            <SelectItem key={_.id} value={_.id}>
                                <Checkbox checked={roomsSelected.includes(_.id)} />
                                {`${_.building && !hideBuildingFromRoomsOptions ? `${_.building.name} - ` : ''}${_.area && !hideAreaFromRoomsOptions ? `${_.area.name} - ` : ''}${_.name}`}
                            </SelectItem>
                        ))
                    }
                </Select>
                }
            </View>
        </View>}
        {showDashboardIndicators && <View className={style.ContentHeaderSection}>
            <View className={style.ContentHeaderSectionItem} >
                <Text type='h4'>Indicadores</Text>
                {loadingIndicators
                ? <Skeleton color='bg3'/>
                : <Select style={{textAlign: 'left'}} size='small' fullWidth multiple value={dashboardIndicatorsSelected} onChange={e => handleDashboardIndicatorsChange(e.target.value)} renderValue={(selected) => _dashboardIndicators?.filter((_,index)=> selected.includes(index)).map(_ => _.indicator.name).join(', ')}>
                    {_dashboardIndicators.length <= 2 && <SelectItem key={'_'} value={'_'}>Nenhum indicador encontrado</SelectItem>}
                    {_dashboardIndicators.length >= 2 && <SelectItem key={'select_all'} value={'select_all'}>Selecionar todos</SelectItem>}
                    {_dashboardIndicators.length >= 2 && <SelectItem key={'clear_all'} value={'clear_all'}>Desmarcar todos</SelectItem>}
                    {_dashboardIndicators?.sort((a,b) => a.indicator.name.localeCompare(b.indicator.name)).map((_, index) => <SelectItem key={index} value={index}><Checkbox checked={dashboardIndicatorsSelected.includes(index)} />{`${_.indicator.name}${_.type === 'line-chart' ? ' (gráfico)' : ''}`}</SelectItem>)}
                </Select>
                }
            </View>
        </View>}
        {showIndicators && <View className={style.ContentHeaderSection}>
            <View className={style.ContentHeaderSectionItem} >
                <Text type='h4'>Indicadores</Text>
                {loadingIndicators
                ? <Skeleton color='bg3'/>
                : <Select style={{textAlign: 'left'}} size='small' fullWidth multiple value={indicatorsSelected} onChange={e => handleIndicatorsChange(e.target.value)} renderValue={(selected) => _indicators?.filter((_,index)=> selected.includes(_.id)).map(_ => _.name).join(', ')}>
                    {(_indicators && _indicators.length <= 2 )&& <SelectItem key={'_'} value={'_'}>Nenhum indicador encontrado</SelectItem>}
                    {(_indicators && _indicators.length >= 2 )&& <SelectItem key={'select_all'} value={'select_all'}>Selecionar todos</SelectItem>}
                    {(_indicators && _indicators.length >= 2 )&& <SelectItem key={'clear_all'} value={'clear_all'}>Desmarcar todos</SelectItem>}
                    {_indicators?.sort((a,b) => a.name.localeCompare(b.name)).map(_ => <SelectItem key={_.id} value={_.id}><Checkbox checked={indicatorsSelected.includes(_.id)} />{_.name}</SelectItem>)}
                </Select>
                }
            </View>
        </View>}
        {(showStartDate || showEndDate )&& <View className={style.ContentHeaderSection}>
            {showStartDate && <View className={style.ContentHeaderSectionItem} >
                <Text type='h4'>Inicio:</Text>
                <DatePicker
                    views={dateType && dateType === 'datetime' ? ['year', 'month', 'day', 'hours', 'minutes'] : ['month', 'year']}
                    sx={{width: '100%'}}
                    minDate={minDate ?? new Date(2010,0,1)}
                    maxDate={maxDate ?? endDate}
                    value={startDate}
                    onChange={date => setStartDate(date ?? undefined)}
                    slotProps={{ textField: { error: !!startDateError, helperText: startDateError, fullWidth: true } }}
                />
            </View>}
            {showEndDate && <View className={style.ContentHeaderSectionItem} >
                <Text type='h4'>Fim:</Text>
                <DatePicker
                    views={dateType && dateType === 'datetime' ? ['year', 'month', 'day', 'hours', 'minutes'] : ['month', 'year']}
                    sx={{width: '100%'}}
                    minDate={minDate ?? startDate}
                    maxDate={maxDate ?? new Date()}
                    value={endDate}
                    onChange={date => setEndDate(date ?? undefined)}
                    slotProps={{ textField: { error: !!endDateError, helperText: endDateError, fullWidth: true } }}
                />
            </View>}
        </View>}
        {(showDate)&& <View className={style.ContentHeaderSection}>
            <View className={style.ContentHeaderSectionItem} >
                <Text type='h4'>Data:</Text>
                <DatePicker 
                    views={dateType && dateType === 'datetime' ? ['year', 'month', 'day', 'hours', 'minutes'] : ['month', 'year']} 
                    sx={{width: '100%'}} 
                    minDate={minDate ?? new Date(2010,0,1)} 
                    maxDate={maxDate ?? new Date()} 
                    value={date} 
                    onChange={date => setDate(date ?? undefined)} 
                    // shouldDisableDate={(day) => {
                    //     if(!enabledDates || enabledDates.length === 0) return false
                    //     const isEnabled = enabledDates.some(enabledDate => 
                    //         dateType === 'datetime'
                    //         ? day.getFullYear() === enabledDate.getFullYear() && day.getMonth() === enabledDate.getMonth() && day.getDate() === enabledDate.getDate()
                    //         : day.getFullYear() === enabledDate.getFullYear() && day.getMonth() === enabledDate.getMonth()
                    //     )
                    //     return !isEnabled
                    //     // if(dateType === 'datetime' && enabledDates && enabledDates.some(enabledDate => !isSameDay(day, enabledDate))) return true
                        
                    //     // if(dateType === 'month' && enabledDates && enabledDates.some(enabledDate => !isSameMonth(day, enabledDate))) return true
                    //     // return false
                    // }}
                    shouldDisableMonth={(month) => {
                        if(!enabledDates) return false
                        const isEnabled = !enabledDates?.some(enabledDate => isInSameMonth(enabledDate, month))
                        return !!isEnabled
                    }}
                    shouldDisableYear={(year) => {
                        if(!enabledDates) return false
                        const isEnabled = !enabledDates?.some(enabledDate => isInSameYear(enabledDate, year))
                        return !!isEnabled
                    }}
                    onError={newError => setDateError(newError ? dateErrorMessage ?? 'Data inválida' : '')}
                    slotProps={{
                        textField: {
                            helperText: dateError
                        }
                    }}
                />
            </View>
        </View>}
        {showOccupationStatus && <View className={style.ContentHeaderSection}>
            <View className={style.ContentHeaderSectionItem} >
                <Text type='h4'>Status de Ocupação:</Text>
                <Select style={{textAlign: 'left'}} size='small' fullWidth value={occupationStatus} onChange={e => setOccupationStatus(e.target.value as 'ocupado' | 'desocupado' | undefined)} >
                    <SelectItem value={undefined}>Todos</SelectItem>
                    <SelectItem value={'ocupado'}>Ocupado</SelectItem>
                    <SelectItem value={'desocupado'}>Desocupado</SelectItem>
                </Select>
            </View>
        </View>}
        {showCapacity && <View className={style.ContentHeaderSection}>
            <View className={style.ContentHeaderSectionItem} >
                <Text type='h4'>Capacidade:</Text>
                <View className={style.CapacityRow}>
                    <View className={style.CapacityItem}>
                        <Text type='h5'>Mínima:</Text>
                        <TextInput type='number' inputProps={{ min: 0 }} fullWidth value={minCapacity} onChange={e => setMinCapacity(e.target.value ? parseInt(e.target.value) : undefined)} />
                    </View>
                    <View className={style.CapacityItem}>
                        <Text type='h5'>Máxima:</Text>
                        <TextInput type='number' inputProps={{ min: 0 }} fullWidth value={maxCapacity} onChange={e => setMaxCapacity(e.target.value ? parseInt(e.target.value) : undefined)} />
                    </View>
                </View>
            </View>
        </View>}
        {showEquipaments && <View className={style.ContentHeaderSection}>
            <View className={style.ContentHeaderSectionItem} >
                <Text type='h4'>Equipamentos:</Text>
                {loadingEquipaments
                ? <Skeleton color='bg3'/>
                : <Select style={{textAlign: 'left'}} size='small' fullWidth multiple value={equipamentsSelected} onChange={e => handleEquipamentsChange(e.target.value)} renderValue={(selected) => equipaments?.filter((_,index)=> selected.includes(_.id)).map(_ => _.name).join(', ')}>
                    {(equipaments && equipaments.length === 0 )&& <SelectItem key={'_'} value={'_'}>Nenhum equipamento encontrado</SelectItem>}
                    {(equipaments && equipaments.length >= 2 )&& <SelectItem key={'select_all'} value={'select_all'}>Selecionar todos</SelectItem>}
                    {(equipaments && equipaments.length >= 2 )&& <SelectItem key={'clear_all'} value={'clear_all'}>Desmarcar todos</SelectItem>}
                    {equipaments?.sort((a,b) => a.name.localeCompare(b.name)).map(_ => <SelectItem key={_.id} value={_.id}><Checkbox checked={equipamentsSelected.includes(_.id)} />{_.name}</SelectItem>)}
                </Select>
                }
            </View>
        </View>}
    </View>
</View>
}